/**
 * PaymentDetailModal — Detalle de un pago de cliente
 * Muestra: datos del pago, estado, asignaciones a facturas, acciones (editar/confirmar/anular).
 */
import { useEffect, useState } from 'react'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { CheckCircle2, XCircle, Loader2, PencilLine } from 'lucide-react'
import { fmtDate, fmtDateTime } from '@/lib/utils'
import { DataTable } from '@/components/ds/DataTable'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import {
  useCustomerPayment, useConfirmPayment, useCancelPayment, useUpdatePayment,
  useCustomerPayments,
  type CustomerPayment,
} from '../hooks/useSales'
import { PaymentModal } from './PaymentModal'
import { displayLabelCls as labelCls } from '@/lib/formStyles'
import { usePermission } from '@/hooks/usePermission'
import { Modal } from '@/components/ds/Modal'

interface PaymentDetailModalProps {
  paymentId: string
  onClose: () => void
}

const PAY_STATUS: Record<string, { label: string; cls: string }> = {
  registered: { label: 'Registrado', cls: 'bg-warning-surface text-warning-content border-warning-border' },
  confirmed:  { label: 'Confirmado', cls: 'bg-success-surface text-success-content border-success-border' },
  cancelled:  { label: 'Anulado',    cls: 'bg-danger-surface text-danger-content border-danger-border' },
}

const fieldCls = 'w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface-muted text-content min-h-[38px]'

/**
 * Historial de cobros asignados a la misma factura. Útil con pagos parciales:
 * muestra cuándo, cómo, quién registró y monto de cada cobro.
 */
function CollectionHistory({ documentId, currentId }: { documentId: string; currentId: string }) {
  const locale = useTenantLocale()
  const { data } = useCustomerPayments({ document_id: documentId, page_size: 100 })
  const items = (data?.payments ?? [])
    .filter(p => p.status !== 'cancelled')
    .sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1))

  if (items.length <= 1) return null

  return (
    <div>
      <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">
        Historial de cobros de la factura ({items.length})
      </p>
      <DataTable<typeof items[number]>
        columns={[
          { key: 'payment_date', header: 'Fecha',
            render: p => <span className="text-content-muted">{fmtDate(p.payment_date, locale)}</span> },
          { key: 'payment_method', header: 'Método',
            render: p => (
              <span className="text-content-muted">{p.payment_method_display ?? p.payment_method}</span>
            ) },
          { key: 'created_by_name', header: 'Registró',
            render: p => <span className="text-content-muted">{p.created_by_name || '—'}</span> },
          { key: 'amount', header: 'Monto', numeric: true,
            render: p => (
              <span className="text-content">
                ${formatNumber(parseFloat(p.amount), locale, parseFloat(p.amount) % 1 !== 0 ? 2 : 0)}
              </span>
            ) },
        ]}
        rows={items}
        rowKey={p => p.id}
        minWidth={480}
        // El pago que se está mirando queda marcado: es lo que orienta al leer
        // la lista de los otros pagos del mismo cliente.
        selectedKey={currentId}
        caption="Otros pagos registrados del mismo cliente"
      />
    </div>
  )
}

export function PaymentDetailModal({ paymentId, onClose }: PaymentDetailModalProps) {
  const locale = useTenantLocale()
  const [editMode, setEditMode] = useState(false)

  const fmt = (value: string | undefined) => {
    if (!value) return '—'
    const n = parseFloat(value)
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(n, locale, decimals)}`
  }
  const { data: payment, isLoading } = useCustomerPayment(paymentId)
  const confirmPayment = useConfirmPayment()
  const cancelPayment = useCancelPayment()
  const updatePayment = useUpdatePayment(paymentId)
  const { confirm: confirmAction, modalProps } = useConfirmModal()
  const { can } = usePermission()
  const canEditConfirmed = can('sales.collections', 'edit_confirmed')

  const handleSaveEdit = (formData: Record<string, unknown>) => {
    updatePayment.mutate(formData as Parameters<typeof updatePayment.mutate>[0], {
      onSuccess: () => {
        setEditMode(false)
        onClose()
      },
      onError: (e: any) => {
        const msg = e?.response?.data?.error?.message || 'No se pudo guardar la edición.'
        window.alert(msg)
      },
    })
  }

  const handleConfirm = () => {
    if (!payment) return
    confirmAction({
      message: `¿Confirmar el pago de ${fmt(payment.amount)} de ${payment.customer_name}?`,
      variant: 'success',
      onConfirm: () => confirmPayment.mutate(payment.id),
    })
  }

  const handleCancel = () => {
    if (!payment) return
    confirmAction({
      message: `¿Anular el pago de ${fmt(payment.amount)} de ${payment.customer_name}?`,
      variant: 'danger',
      onConfirm: () => cancelPayment.mutate(payment.id),
    })
  }

  const statusCfg = payment ? PAY_STATUS[payment.status] ?? PAY_STATUS.registered : PAY_STATUS.registered
  const totalAllocated = payment?.allocations?.reduce((s, a) => s + parseFloat(a.amount || '0'), 0) ?? 0
  const unallocated = payment ? parseFloat(payment.amount) - totalAllocated : 0

  // Editable si está registrado, o confirmado con el permiso especial edit_confirmed.
  const canEnterEdit = payment?.status === 'registered'
    || (payment?.status === 'confirmed' && canEditConfirmed)
  useEffect(() => {
    if (editMode && !canEnterEdit) {
      setEditMode(false)
    }
  }, [editMode, canEnterEdit])

  const handleEditConfirmed = () => {
    confirmAction({
      message: 'Se va a EDITAR un cobro CONFIRMADO. Esto actualizará su comprobante contable EN SITIO (sin reversa) y recalculará la cobranza. Solo aplica en períodos abiertos. ¿Continuar?',
      variant: 'danger',
      confirmLabel: 'Editar de todas formas',
      onConfirm: () => setEditMode(true),
    })
  }

  if (editMode && payment) {
    return (
      <PaymentModal
        payment={payment}
        onSave={handleSaveEdit}
        onClose={() => setEditMode(false)}
        isPending={updatePayment.isPending}
      />
    )
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title="Detalle de Pago"
        subtitle={payment && (
          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusCfg.cls}`}>
            {statusCfg.label}
          </span>
        )}
        actions={
          <>
            <div className="flex gap-2 mr-auto">
              {payment?.status === 'registered' && (
                <>
                  <button
                    onClick={() => setEditMode(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-content border border-divider rounded-lg hover:bg-soft transition-colors">
                    <PencilLine size={14} />
                    Editar
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={confirmPayment.isPending}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-success-fill text-white rounded-lg hover:bg-success-fill-hover transition-colors disabled:opacity-50">
                    {confirmPayment.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Confirmar Pago
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={cancelPayment.isPending}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface transition-colors disabled:opacity-50">
                    {cancelPayment.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                    Anular
                  </button>
                </>
              )}
              {payment?.status === 'confirmed' && (
                <>
                  {canEditConfirmed && (
                    <button
                      onClick={handleEditConfirmed}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-rose-600 dark:text-rose-300 border border-rose-200 dark:border-rose-800 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors">
                      <PencilLine size={14} />
                      Editar (confirmado)
                    </button>
                  )}
                  <button
                    onClick={handleCancel}
                    disabled={cancelPayment.isPending}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface transition-colors disabled:opacity-50">
                    {cancelPayment.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                    Anular Pago
                  </button>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
              Cerrar
            </button>
          </>
        }
      >
          <div className="space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-content-subtle">
                <Loader2 size={20} className="animate-spin mr-2" />Cargando...
              </div>
            ) : payment ? (
              <>
                {/* Datos del pago */}
                <div>
                  <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Información del Pago</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className={labelCls}>Cliente</p>
                      <div className={fieldCls}>{payment.customer_name}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Fecha de Pago</p>
                      <div className={fieldCls}>{fmtDate(payment.payment_date, locale)}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Monto</p>
                      <div className={`${fieldCls} font-semibold text-content`}>{fmt(payment.amount)}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Método de Pago</p>
                      <div className={fieldCls}>{payment.payment_method_display ?? payment.payment_method}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Referencia</p>
                      <div className={fieldCls}>{payment.reference || '—'}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Estado</p>
                      <div className={fieldCls}>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.cls}`}>
                          {statusCfg.label}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className={labelCls}>Cuenta Contable (Banco)</p>
                      <div className={fieldCls}>
                        {payment.bank_account_display || <span className="text-content-subtle italic">Sin asignar — se usará la cuenta por defecto al confirmar</span>}
                      </div>
                    </div>
                    <div>
                      <p className={labelCls}>Estado Contable</p>
                      <div className={fieldCls}>
                        {payment.accounting_status === 'accounted' ? (
                          <span className="inline-flex items-center gap-1 text-success-content-soft font-medium">
                            <CheckCircle2 size={14} /> Contabilizado
                          </span>
                        ) : (
                          <span className="text-content-subtle">Pendiente</span>
                        )}
                      </div>
                    </div>
                    {payment.notes && (
                      <div className="sm:col-span-2">
                        <p className={labelCls}>Notas</p>
                        <div className={fieldCls}>{payment.notes}</div>
                      </div>
                    )}
                    {payment.created_by_name && (
                      <div>
                        <p className={labelCls}>Registrado por</p>
                        <div className={fieldCls}>{payment.created_by_name}</div>
                      </div>
                    )}
                    <div>
                      <p className={labelCls}>Fecha de Registro</p>
                      <div className={fieldCls}>{fmtDateTime(payment.created_at, locale)}</div>
                    </div>
                  </div>
                </div>

                {/* Asignaciones */}
                <div>
                  <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">
                    Asignación a Facturas
                  </p>
                  {payment.allocations && payment.allocations.length > 0 ? (
                    <>
                      <DataTable<typeof payment.allocations[number]>
                        columns={[
                          { key: 'invoice_folio', header: 'Folio',
                            render: alloc => (
                              <span className="text-content">
                                {alloc.invoice_folio ? `Folio ${alloc.invoice_folio}` : alloc.invoice.slice(0, 8)}
                              </span>
                            ) },
                          { key: 'amount', header: 'Monto Aplicado', numeric: true,
                            render: alloc => <span className="font-medium text-content">{fmt(alloc.amount)}</span> },
                          { key: 'created_at', header: 'Fecha',
                            render: alloc => (
                              <span className="text-content-muted">{fmtDate(alloc.created_at, locale)}</span>
                            ) },
                        ]}
                        rows={payment.allocations}
                        rowKey={alloc => alloc.id}
                        minWidth={420}
                        caption="Facturas a las que se aplicó este pago"
                      />
                      <div className="mt-2 flex justify-end gap-4 text-sm">
                        <div className="text-right">
                          <p className="text-xs text-content-subtle">Asignado</p>
                          <p className="font-medium text-content">{fmt(String(totalAllocated))}</p>
                        </div>
                        {unallocated > 0.01 && (
                          <div className="text-right">
                            <p className="text-xs text-warning-solid">Sin asignar</p>
                            <p className="font-medium text-warning-content-soft">{fmt(String(unallocated))}</p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-content-subtle">Este pago no tiene asignaciones a facturas.</p>
                  )}
                </div>

                {payment.allocations?.[0]?.invoice && (
                  <CollectionHistory documentId={payment.allocations[0].invoice} currentId={payment.id} />
                )}
              </>
            ) : (
              <p className="text-center text-content-subtle py-8">Pago no encontrado.</p>
            )}
          </div>
      </Modal>
      <ConfirmModal {...modalProps} />
    </>
  )
}
